import { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { ESmsLogStatuses, TSmsLog, TSmsLogType } from 'src/types/sms-log'
import { TPatient } from 'src/types/patient'
import { useSocket } from 'src/providers/socket.provider'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { CurecallBadge, ECurecallBadgeColor, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { SmsLogCard } from './sms-log-card.component'
import { PatientService } from 'src/services/patient.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useSession } from 'src/providers/auth.provider'
import CurecallMessenger from './curecall-messenger.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { capitalizeFirstLetter } from 'src/utils/string-formatter.util'
import { useTranslation } from 'react-i18next'
import { TPatientFollowup } from 'src/types/followup'
import { useNotification } from 'src/providers/notification.provider'
import SendSMS from './send-sms.component'
import { config } from 'src/config'

type TConsultSMSProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  currentPatientFollowup: Pick<TPatientFollowup, 'id' | 'FollowupId' | 'endReason'>
  filterByPatientFollowupId?: number
}

const NUMBER_OF_SMS_PER_QUERY = 10

const ConsultSMS = ({ patient, currentPatientFollowup, filterByPatientFollowupId }: TConsultSMSProps) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { socket, socketError } = useSocket()
  const { currentUser } = useSession()
  const { notify } = useNotification()

  const [messagesToShow, setMessagesToShow] = useState<TSmsLog[]>([])

  const {
    isLoading: isLoadingSMS,
    error,
    data: patientSMS,
  } = useQuery<TSmsLog[]>({
    queryKey: ['patient-sms', project.id, patient.id, filterByPatientFollowupId],
    queryFn: () =>
      PatientService.getSMS(project.id, patient.id, 0, NUMBER_OF_SMS_PER_QUERY, filterByPatientFollowupId),
  })

  useEffect(() => {
    if (Array.isArray(patientSMS)) {
      setMessagesToShow(patientSMS)
    }
  }, [patientSMS])

  useEffect(() => {
    if (!socket || !patient.id || socketError) return undefined
    const onNewSmsEvent = async ({ patientId: WSPatientId, sms }: any) => {
      if (patient.id !== WSPatientId) return
      setMessagesToShow((prev) => [sms, ...prev])
    }
    const onSmsStatusChange = async ({
      patientId: WSPatientId,
      smsLogId,
      newStatus,
    }: {
      patientId: number
      smsLogId: number
      newStatus: ESmsLogStatuses
    }) => {
      if (patient.id !== WSPatientId) return
      setMessagesToShow((prev) =>
        prev.map((sms) => (sms.id === smsLogId ? { ...sms, status: newStatus } : sms)),
      )
    }

    const onSmsTypeChange = ({
      patientId: WSPatientId,
      smsLogId,
      newType,
    }: {
      patientId: number
      smsLogId: number
      newType: TSmsLogType
    }) => {
      if (patient.id !== WSPatientId) return
      setMessagesToShow((prev) => prev.map((sms) => (sms.id === smsLogId ? { ...sms, type: newType } : sms)))
    }

    socket.on('NEW_SMS', onNewSmsEvent)
    socket.on('SMS_STATUS_CHANGE', onSmsStatusChange)
    socket.on('SMS_TYPE_CHANGE', onSmsTypeChange)
    return () => {
      socket.off('NEW_SMS', onNewSmsEvent)
      socket.off('SMS_STATUS_CHANGE', onSmsStatusChange)
      socket.off('SMS_TYPE_CHANGE', onSmsTypeChange)
    }
  }, [socket, socketError, patient.id, project.id])

  const handleFetchNext = async () => {
    try {
      const moreOlderSms = await PatientService.getSMS(
        project.id,
        patient.id,
        messagesToShow.length,
        NUMBER_OF_SMS_PER_QUERY,
        filterByPatientFollowupId,
      )
      if (moreOlderSms.length > 0) {
        setMessagesToShow((prev) => {
          const updatedMessages = [...prev, ...moreOlderSms]
          return updatedMessages
        })
      }
    } catch (error: any) {
      notify({
        title: t('components.sms.error-on-fetching'),
        message: error,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const isCurecallMessengerActivated = config.IS_CURECALL_MESSENGER_ACTIVATED

  if (error) return <ErrorQueryHandler error={error} />

  return (
    <div className="c-flex c-flex-col c-min-h-0">
      {messagesToShow?.length ? (
        <div
          id="scrollableDiv"
          style={{
            height: '50vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
          className="c-bg-grey-50 c-w-full c-min-w-full c-pt-6 c-px-8 c-pb-4"
        >
          <InfiniteScroll
            loader={isLoadingSMS}
            next={handleFetchNext}
            dataLength={messagesToShow.length}
            hasMore={true}
            style={{
              display: 'flex',
              flexDirection: 'column-reverse',
            }}
            inverse={true}
            scrollableTarget="scrollableDiv"
            className="p-4"
          >
            {messagesToShow.map((smsLog, index) => {
              let shouldShowDateIndicator = false
              if (index < messagesToShow.length - 1) {
                const nextMessage = messagesToShow[index + 1] // the next message is the older message in time (and the upper message in window)
                if (nextMessage?.sentAt && smsLog?.sentAt) {
                  const isADifferentMonth = !dayjs(smsLog.sentAt).isSame(nextMessage?.sentAt, 'month')
                  const isADifferentYear = !dayjs(smsLog.sentAt).isSame(nextMessage?.sentAt, 'year')
                  shouldShowDateIndicator = isADifferentMonth || isADifferentYear
                }
              }
              if (index === messagesToShow.length - 1) {
                // this is the last message (first message at top)
                shouldShowDateIndicator = true
              }
              return (
                <div
                  key={smsLog.id}
                  className={twMerge(
                    'c-flex c-flex-col-reverse',
                    index === messagesToShow.length - 1 && 'c-mt-20',
                  )}
                >
                  <SmsLogCard smsLog={smsLog} patient={patient} />
                  {shouldShowDateIndicator && (
                    <CurecallBadge
                      color={ECurecallBadgeColor.SMOOTH_PRIMARY}
                      className="c-my-3 c-self-center"
                    >
                      {capitalizeFirstLetter(dayjs(smsLog.sentAt).format('MMMM YYYY'))}
                    </CurecallBadge>
                  )}
                </div>
              )
            })}
          </InfiniteScroll>
        </div>
      ) : (
        <p className="c-bg-grey-50 c-text-base c-w-full c-min-w-full c-h-80 c-text-center c-pt-36 c-px-8 c-pb-4 c-mb-0">
          {t('components.sms.no-message-yet')}
        </p>
      )}
      <div className="c-flex c-pt-6 c-gap-6 c-px-8 c-pb-4 c-border-t c-border-t-grey-200">
        {!currentPatientFollowup?.endReason && (
          <>
            <SendSMS patient={patient} currentPatientFollowup={currentPatientFollowup} />
            {isCurecallMessengerActivated && currentUser?.isAdmin && (
              <CurecallMessenger patientId={patient.id} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ConsultSMS
